/**
 * Edit Payment Dialog Component
 * Colaiste Lurgan Management System - Edit existing student payments
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { useUpdatePayment } from '../../hooks/usePayments';
import { Pencil, Loader2, X } from 'lucide-react';

// Payment types
const PAYMENT_TYPES = ['DEPOSIT', 'BALANCE', 'REFUND', 'ADJUSTMENT', 'SCHOLARSHIP'];

// Payment statuses
const PAYMENT_STATUSES = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'CANCELLED', 'REFUNDED'];

// Payment methods
const PAYMENT_METHODS = ['STRIPE', 'CARD', 'BANK_TRANSFER', 'CASH', 'CHEQUE', 'OTHER'];

// Scholarship types
const SCHOLARSHIP_TYPES = ['FULL', 'PARTIAL'];

// Validation schema
const paymentSchema = z.object({
  payment_type: z.enum(PAYMENT_TYPES, {
    required_error: 'Payment type is required',
  }),
  amount: z.coerce
    .number()
    .positive('Amount must be greater than 0')
    .max(10000, 'Amount cannot exceed 10,000'),
  payment_method: z.enum(PAYMENT_METHODS, {
    required_error: 'Payment method is required',
  }),
  status: z.enum(PAYMENT_STATUSES, {
    required_error: 'Status is required',
  }),
  payment_date: z.string().optional(),
  reference_number: z.string().max(50).optional(),
  notes: z.string().max(500).optional(),
  // Scholarship fields
  scholarship_provider: z.string().max(100).optional(),
  scholarship_type: z.enum([...SCHOLARSHIP_TYPES, '']).optional(),
  scholarship_reference: z.string().max(50).optional(),
}).refine((data) => {
  // If payment type is SCHOLARSHIP, provider is required
  if (data.payment_type === 'SCHOLARSHIP' && !data.scholarship_provider) {
    return false;
  }
  return true;
}, {
  message: 'Scholarship provider is required for scholarship payments',
  path: ['scholarship_provider'],
});

/**
 * Format date for input field (YYYY-MM-DD)
 */
function formatDateForInput(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
}

export function EditPaymentDialog({ open, onOpenChange, payment, student }) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      payment_type: 'DEPOSIT',
      amount: '',
      payment_method: 'CARD',
      status: 'COMPLETED',
      payment_date: '',
      reference_number: '',
      notes: '',
      scholarship_provider: '',
      scholarship_type: '',
      scholarship_reference: '',
    },
  });

  const paymentType = watch('payment_type');
  const isScholarship = paymentType === 'SCHOLARSHIP';

  // Reset form with payment data when dialog opens or payment changes
  useEffect(() => {
    if (open && payment) {
      reset({
        payment_type: payment.payment_type || 'DEPOSIT',
        amount: payment.amount || '',
        payment_method: payment.payment_method || 'CARD',
        status: payment.status || 'COMPLETED',
        payment_date: formatDateForInput(payment.payment_date),
        reference_number: payment.reference_number || '',
        notes: payment.notes || '',
        scholarship_provider: payment.scholarship_provider || '',
        scholarship_type: payment.scholarship_type || '',
        scholarship_reference: payment.scholarship_reference || '',
      });
    }
  }, [open, payment, reset]);

  const updatePaymentMutation = useUpdatePayment({
    onSuccess: () => {
      toast({
        title: t('payments.toast.updateSuccess'),
        description: t('payments.toast.updateSuccessDescription'),
        variant: 'success',
      });
      onOpenChange(false);
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.non_field_errors?.[0] ||
        Object.values(error.response?.data || {})[0]?.[0] ||
        t('payments.toast.updateError');
      toast({
        title: t('payments.toast.updateErrorTitle'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data) => {
    if (!payment?.uuid) return;

    // Prepare payment data
    const paymentData = {
      uuid: payment.uuid,
      payment_type: data.payment_type,
      amount: data.amount,
      payment_method: data.payment_method,
      status: data.status,
      payment_date: data.payment_date || null,
      reference_number: data.reference_number || '',
      notes: data.notes || '',
    };

    // Add scholarship fields if applicable
    if (data.payment_type === 'SCHOLARSHIP') {
      paymentData.scholarship_provider = data.scholarship_provider;
      paymentData.scholarship_type = data.scholarship_type || '';
      paymentData.scholarship_reference = data.scholarship_reference || '';
    } else {
      // Clear scholarship fields if not a scholarship
      paymentData.scholarship_provider = '';
      paymentData.scholarship_type = '';
      paymentData.scholarship_reference = '';
    }

    updatePaymentMutation.mutate(paymentData);
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-blue-600" />
            {t('payments.editPaymentTitle')} - {student?.full_name}
          </DialogTitle>
          <DialogDescription>
            {t('payments.editPaymentDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Payment Type */}
          <div className="space-y-2">
            <Label htmlFor="payment_type">{t('payments.form.paymentType')} *</Label>
            <Select
              value={paymentType}
              onValueChange={(value) => setValue('payment_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('payments.form.selectType')} />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`payments.type.${type}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.payment_type && (
              <p className="text-sm text-red-600">{errors.payment_type.message}</p>
            )}
          </div>

          {/* Scholarship Fields - Shown only for SCHOLARSHIP type */}
          {isScholarship && (
            <div className="p-3 bg-purple-50 rounded-lg space-y-3 border border-purple-200">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="scholarship_provider">
                    {t('payments.scholarship.provider')} *
                  </Label>
                  <Input
                    id="scholarship_provider"
                    {...register('scholarship_provider')}
                    placeholder={t('payments.scholarship.providerPlaceholder')}
                  />
                  {errors.scholarship_provider && (
                    <p className="text-sm text-red-600">
                      {errors.scholarship_provider.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scholarship_type">
                    {t('payments.scholarship.type')}
                  </Label>
                  <Select
                    value={watch('scholarship_type')}
                    onValueChange={(value) => setValue('scholarship_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('payments.scholarship.selectType')} />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHOLARSHIP_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {t(`payments.scholarship.${type}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="scholarship_reference">
                  {t('payments.scholarship.reference')}
                </Label>
                <Input
                  id="scholarship_reference"
                  {...register('scholarship_reference')}
                  placeholder={t('payments.scholarship.referencePlaceholder')}
                />
              </div>
            </div>
          )}

          {/* Amount and Payment Method */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">{t('payments.form.amount')} *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  EUR
                </span>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="10000"
                  {...register('amount')}
                  className="pl-12"
                  placeholder="0.00"
                />
              </div>
              {errors.amount && (
                <p className="text-sm text-red-600">{errors.amount.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_method">{t('payments.form.paymentMethod')}</Label>
              <Select
                value={watch('payment_method')}
                onValueChange={(value) => setValue('payment_method', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {t(`payments.method.${method}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Payment Date and Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="payment_date">{t('payments.form.paymentDate')}</Label>
              <Input
                id="payment_date"
                type="date"
                {...register('payment_date')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">{t('payments.form.status')}</Label>
              <Select
                value={watch('status')}
                onValueChange={(value) => setValue('status', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {t(`payments.status.${status}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reference Number */}
          <div className="space-y-2">
            <Label htmlFor="reference_number">
              {t('payments.form.referenceNumber')}
              <span className="text-gray-400 text-xs ml-1">
                ({t('common.optional')})
              </span>
            </Label>
            <Input
              id="reference_number"
              {...register('reference_number')}
              placeholder={t('payments.form.referencePlaceholder')}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">
              {t('payments.form.notes')}
              <span className="text-gray-400 text-xs ml-1">
                ({t('common.optional')})
              </span>
            </Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder={t('payments.form.notesPlaceholder')}
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              <X className="h-4 w-4 mr-2" />
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={updatePaymentMutation.isPending}
            >
              {updatePaymentMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('payments.form.saving')}
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4 mr-2" />
                  {t('payments.form.saveChanges')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditPaymentDialog;
